import React from 'react';
import clsx from 'clsx';
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/16/solid';

interface ErrorAlertProps {
  /** The message. Renders nothing when empty, so `{error}` can go straight in. */
  children?: React.ReactNode;
  /** A short bold lead-in — "Couldn't load reservations". Optional. */
  title?: React.ReactNode;
  /** Shows a "Try again" button. */
  onRetry?: () => void;
  /** Shows a close button. Without it the alert stays until the error clears. */
  onDismiss?: () => void;
  className?: string;
}

/**
 * The one way an error is shown inline (#652) — a failed load, a refused
 * save, a form the server turned down. Kept identical in front-porch and
 * member-portal — edit both, or neither.
 *
 * **It never dismisses itself.** The old `ShowAlert` faded out after a
 * timeout, which on a detail page whose body only renders once the record
 * loads left a blank page and nothing to say why — and a `timeout={0}`
 * closed it before it was ever seen. An error stays until the person
 * dismisses it or the thing that failed succeeds.
 *
 * **Not Catalyst's `ErrorMessage`.** That one is `Headless.Description`
 * underneath and throws — "You used a `<Description />` component, but it
 * is not inside a relevant parent" — whenever it is rendered outside a
 * `<Field>`, and React unmounts the page. It is still the right thing for
 * a single field's validation, *inside* the `<Field>`; this is for
 * everything else.
 *
 * For a page that cannot render at all, use `ErrorPage`.
 */
export const ErrorAlert: React.FC<ErrorAlertProps> = ({
  children,
  title,
  onRetry,
  onDismiss,
  className,
}) => {
  if (!children && !title) return null;
  return (
    <div
      role="alert"
      className={clsx(
        'flex items-start gap-2 rounded-md bg-danger/10 px-3 py-2 text-[13px]/5 text-danger',
        className
      )}
    >
      <ExclamationCircleIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className="break-words">{children}</div>}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1 font-medium underline underline-offset-2 hover:no-underline"
          >
            Try again
          </button>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mr-1 rounded p-0.5 hover:bg-danger/10"
        >
          <XMarkIcon aria-hidden="true" className="size-4" />
        </button>
      )}
    </div>
  );
};

export default ErrorAlert;
